import { type ComponentProps, type ReactNode, useEffect, useRef } from 'react';

import type { ActionMenuItem } from '@rover/components/src/callouts/ActionMenu/ActionMenu.common';
import { Flex } from '@rover/kibble/core';
import ImageGridContainer from '@rover/react-lib/src/components/ImageGallery/ImageGridContainer';

import type { BulkPrivacyChange } from './hooks/useGalleryManageMode';
import useGalleryManageMode from './hooks/useGalleryManageMode';
import { GALLERY_CONTAINER_WIDTH } from './constants';
import GallerySelectionBar from './GallerySelectionBar';
import type { GalleryItem, PaginationConfig } from './ImageGallery.common';
import { GalleryVisibility, isEditableGalleryImage, isLockedImage } from './ImageGallery.common';
import ImageGridManageControls from './ImageGridManageControls';

type Props = {
  images: GalleryItem[] | undefined;
  onImageClick: (index: number) => void;
  onClose?: () => void;
  pagination?: PaginationConfig;
  renderImageOverlay?: (index: number) => ReactNode;
  /** Web gets `File`s and native gets `MediaFile`s, as `ImageGridContainer` does. */
  onImagesAdded?: ComponentProps<typeof ImageGridContainer>['onImagesAdded'];
  isUploading?: boolean;
  renderImageActions?: (index: number) => ActionMenuItem[];
  columns?: number;
  onReorder?: (fromIndex: number, toIndex: number) => void;
  isReorderable?: (index: number) => boolean;
  /** Native only. See `ImageGridContainer.native`. */
  virtualizedGrid?: boolean;
  dimmedImageIds?: Set<number>;
  lockedImageIds?: Set<number>;
  /** Whether these photos are the viewer's to curate. The host decides. */
  isViewingOwnGallery?: boolean;
  onBulkPrivacyChange?: BulkPrivacyChange;
  visibility?: GalleryVisibility;
  /** Drawn at the end of the header row, beside the manage controls. */
  headerEnd?: ReactNode;
  /** Drawn at the start of the header row, opposite the manage controls. */
  gridHeader?: ReactNode;
  /** Set when the grid sits on a dark background, as it does in the lightbox. */
  contrast?: boolean;
  /** Drawn in place of the tiles while the host reads them, under the same header. */
  loadingGrid?: ReactNode;
};

// The controls live here so the grid behaves the same in either host.
const ManagedGalleryGrid = ({
  images,
  isViewingOwnGallery = false,
  onBulkPrivacyChange,
  visibility = GalleryVisibility.All,
  headerEnd,
  gridHeader,
  contrast = false,
  dimmedImageIds,
  lockedImageIds,
  loadingGrid,
  ...gridProps
}: Props): JSX.Element => {
  const { isManaging, selectedIds, enterManageMode, cancel, toggleSelected, setSelectedPrivacy } =
    useGalleryManageMode(onBulkPrivacyChange);

  // A selection made in one slice would act on photos no longer on show.
  const shownVisibility = useRef(visibility);
  useEffect(() => {
    if (shownVisibility.current === visibility) return;
    shownVisibility.current = visibility;
    if (isManaging) cancel();
  }, [visibility, isManaging, cancel]);

  const canManage =
    isViewingOwnGallery &&
    !!onBulkPrivacyChange &&
    !!images?.some(
      (image) => isEditableGalleryImage(image) && !isLockedImage(image, lockedImageIds)
    );

  // Done is hidden along with Manage photos, so manage mode ends here.
  useEffect(() => {
    if (!canManage && isManaging) cancel();
  }, [canManage, isManaging, cancel]);

  return (
    <Flex
      flexDirection="column"
      flex={gridProps.onClose ? '1' : undefined}
      width="100%"
      justifyContent="center"
      gap="6x"
    >
      {(canManage || headerEnd || gridHeader) && (
        <Flex
          flexDirection="column"
          width="100%"
          maxWidth={GALLERY_CONTAINER_WIDTH}
          gap="2x"
          alignItems="flex-end"
          sx={{ marginLeft: 'auto', marginRight: 'auto' }}
        >
          <ImageGridManageControls
            canManage={canManage}
            isManaging={isManaging}
            contrast={contrast}
            gridHeader={gridHeader}
            headerEnd={headerEnd}
            onEnterManageMode={enterManageMode}
            onCancel={cancel}
          />
        </Flex>
      )}
      {/* The bar sticks only within its parent, so it shares one with the grid it scrolls over. */}
      <Flex flexDirection="column" width="100%">
        {canManage && isManaging && selectedIds.size > 0 && (
          <GallerySelectionBar
            selectedCount={selectedIds.size}
            contrast={contrast}
            onMakePrivate={
              visibility === GalleryVisibility.Private ? undefined : () => setSelectedPrivacy(false)
            }
            onMakePublic={
              visibility === GalleryVisibility.Public ? undefined : () => setSelectedPrivacy(true)
            }
          />
        )}
        {loadingGrid}
        {!loadingGrid && images && (
          <ImageGridContainer
            images={images}
            dimmedImageIds={dimmedImageIds}
            lockedImageIds={lockedImageIds}
            isManaging={isManaging}
            selectedIds={selectedIds}
            onToggleSelected={toggleSelected}
            {...gridProps}
          />
        )}
      </Flex>
    </Flex>
  );
};

export default ManagedGalleryGrid;
